import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Check } from "lucide-react";

const ROUTE_FOR = {
  profile_avatar: "/app/profile",
  invite_team: "__invite__",              // opens invite dialog via `onInvite`
  first_channel: "/app/channels",
  enter_office: "/app/office",
  first_task: "/app/tasks",
};

/**
 * Dismissible activation checklist. Non-blocking. Never displays fake progress.
 * Steps are computed server-side from real state.
 */
export default function ActivationChecklist({ onInvite }) {
  const [state, setState] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/activation-checklist").then((r) => { if (!cancelled) setState(r.data); }).catch(() => setState(null));
    return () => { cancelled = true; };
  }, []);

  if (!state || state.dismissed || state.done === state.total) return null;

  const dismiss = async () => {
    try { await api.post("/activation-checklist/dismiss"); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[checklist] dismiss failed", err);
    }
    setState({ ...state, dismissed: true });
  };
  const clickStep = (step) => {
    if (step.done) return;
    const target = ROUTE_FOR[step.key];
    if (target === "__invite__") onInvite && onInvite();
    else if (target) nav(target);
  };

  if (minimized) {
    return (
      <button
        className="fixed bottom-5 right-5 z-30 rounded-full bg-zinc-900 text-white text-xs font-medium px-4 h-10 shadow-lg hover:bg-black"
        onClick={() => setMinimized(false)}
        data-testid="activation-checklist-expand"
      >
        Setup · {state.done}/{state.total}
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-5 right-5 z-30 w-[320px] max-w-[calc(100vw-2rem)] rounded-xl bg-white border border-zinc-200 shadow-xl overflow-hidden"
      data-testid="activation-checklist"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-zinc-950 text-white">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-400">Getting started</div>
          <div className="text-sm font-semibold">{state.done} of {state.total} complete</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(true)} className="text-zinc-400 hover:text-white text-xs px-2 py-1" data-testid="activation-checklist-minimize">
            Minimize
          </button>
          <button onClick={dismiss} className="text-zinc-400 hover:text-white p-1" aria-label="Dismiss" data-testid="activation-checklist-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-2">
        {state.steps.map((s) => (
          <button
            key={s.key}
            onClick={() => clickStep(s)}
            data-testid={`checklist-step-${s.key}`}
            className={`w-full flex items-center gap-3 rounded-lg text-left px-3 py-2 transition ${
              s.done ? "opacity-70 cursor-default" : "hover:bg-zinc-50"
            }`}
            disabled={s.done}
          >
            <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border ${
              s.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-zinc-300"
            }`}>
              {s.done && <Check className="w-3 h-3" />}
            </span>
            <span className={`text-sm ${s.done ? "line-through text-zinc-500" : "text-zinc-800"}`}>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
